import { useCallback, useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import { meetingTranscript } from './api'
import type { MeetingTranscript } from './types'

/** Slow: transcription takes minutes, and nobody watches this tab waiting for a word. */
const POLL_MS = 15000

/**
 * What was said.
 *
 * ## Speakers come for free
 *
 * Each line carries a name because each recording is one person — the payoff of recording
 * per participant rather than as a composite of the room. A composite would have been one
 * audio track of everybody and would need diarisation to guess at the same answer, badly,
 * in exactly the meeting where two people talk over each other.
 *
 * ## Every state says something different
 *
 * "Not recorded", "queued", "running" and "failed" are four different things to tell
 * somebody who is waiting, and a single empty state would tell them none of it.
 */
export function TranscriptPanel({ meetingId }: { meetingId: string }) {
  const [transcript, setTranscript] = useState<MeetingTranscript | null>(null)
  const [error, setError] = useState<string | null>(null)

  const pull = useCallback(() => {
    meetingTranscript(meetingId).then(setTranscript).catch((e) => setError(explain(e)))
  }, [meetingId])

  useEffect(() => {
    pull()
    const t = setInterval(pull, POLL_MS)
    return () => clearInterval(t)
  }, [pull])

  if (error) return <ErrorStrip message={error} />
  if (!transcript) return <p className="py-4 text-center text-erp-xs text-muted-foreground">Loading…</p>

  if (transcript.lines.length === 0) {
    return <Waiting state={transcript.stateCode} reason={transcript.reason} />
  }

  return (
    <div className="space-y-3">
      {transcript.language && (
        <p className="text-erp-xs text-muted-foreground">
          Detected language: {transcript.language}. Machine-made, and worth reading as a guide
          to the recording rather than as a record of it.
        </p>
      )}
      <ul className="space-y-2">
        {transcript.lines.map((line, i) => (
          <li key={`${line.recordingId}-${line.startMs}-${i}`} className="text-erp-sm">
            <span className="mr-2 font-medium">{line.speaker}</span>
            <span className="text-erp-xs text-muted-foreground">{clock(line.startMs)}</span>
            <p className="whitespace-pre-wrap break-words">{line.text}</p>
          </li>
        ))}
      </ul>
    </div>
  )
}

function Waiting({ state, reason }: { state: string; reason: string | null }) {
  switch (state) {
    case 'SKIPPED':
      return (
        <Note tone="info">
          {reason ??
            'The speech was not in a language this transcriber can write down, so no' +
              ' transcript was made.'}{' '}
          The recording itself is unaffected and can be played from the Archive.
        </Note>
      )
    case 'EMPTY':
      return (
        <Note tone="info">
          The recording was transcribed and no speech was found — usually a muted microphone.
        </Note>
      )
    case 'QUEUED':
      return (
        <p className="flex items-center gap-2 py-4 text-erp-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Queued. Transcription starts when the machine is free.
        </p>
      )
    case 'RUNNING':
      return (
        <p className="flex items-center gap-2 py-4 text-erp-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Transcribing. A long meeting takes several minutes.
        </p>
      )
    case 'FAILED':
      return (
        <Note tone="warn">
          The transcription failed. The recording is unaffected — it can be played from the
          Archive, and the attempt can be made again.
        </Note>
      )
    case 'NO_RECORDING':
      return (
        <Note tone="info">
          Nothing was written to disk, so there is nothing to transcribe.
        </Note>
      )
    default:
      return (
        <Note tone="info">
          This meeting was not recorded. A transcript is made from a recording, so turn
          recording on in the Archive tab before the meeting that matters.
        </Note>
      )
  }
}

/** mm:ss from the start of that speaker's own recording. */
function clock(ms: number): string {
  const total = Math.floor(ms / 1000)
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}
